import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Stack,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import api from "../services/api";
import Header from "./Header";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

export default function Category() {
  const [data, setData] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState("");
  const [categoryData, setCategoryData] = useState({
    name: "",
    image: null,
  });

  const checklogin = localStorage.getItem("login");
  const navigate = useNavigate();

  useEffect(() => {
    if (!checklogin) {
      navigate("/");
    }
  }, [checklogin, navigate]);

  const getData = async () => {
    try {
      const res = await api.get("/categories");
      setData(res.data);
    } catch (err) {
      toast.error("Failed to fetch categories.");
      console.error("Error fetching data:", err);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const handleDelete = (id) => {
    setDeleteDialog(true);
    setDeleteItemId(id);
  };

  const confirmDelete = async (id) => {
    try {
      const response = await api.delete(`/deleteCategory/${id}`);
      if (response.status === 200) {
        toast.success(response.data.message);
        getData();
        setDeleteDialog(false);
      } else {
        toast.error("Failed to delete category.");
      }
    } catch (err) {
      console.error("Error deleting category:", err.response || err.message);
      toast.error("Failed to delete category.");
      setDeleteDialog(false);
    }
  };

  const handleOpenAdd = () => {
    setCategoryData({ name: "", image: null });
    setAddDialog(true);
  };

  const handleCloseAdd = () => {
    setAddDialog(false);
    setCategoryData({ name: "", image: null });
  };

  const handleOpenEdit = (params) => {
    const updateCategory = {
      id: params.row.id,
      name: params.row.CategoryName,
      image: params.row.image,
    };
    setCategoryData(updateCategory);
    setEditDialog(true);
  };

  const handleCloseEdit = () => {
    setEditDialog(false);
    setCategoryData({ name: "", image: null });
  };

  const handleFileChange = (e) => {
    setCategoryData({
      ...categoryData,
      image: e.target.files[0],
    });
  };

  const handleAddCategory = async () => {
    if (!categoryData.name) {
      toast.error("Category name is required");
      return;
    }

    const formData = new FormData();
    formData.append("CategoryName", categoryData.name);
    formData.append("image", categoryData.image);

    try {
      const res = await api.post("/categories", formData);
      if (res.data) {
        toast.success(res.data.message);
        handleCloseAdd();
        getData();
      }
    } catch (err) {
      toast.error("Failed to add category");
      handleCloseAdd();
      console.error("Error adding category:", err);
    }
  };

  const handleUpdateCategory = async () => {
    if (!categoryData.name) {
      toast.error("Category name is required");
      return;
    }

    const formData = new FormData();
    formData.append("CategoryName", categoryData.name);
    if (categoryData.image instanceof File) {
      formData.append("image", categoryData.image);
    }

    try {
      await api.put(`/updateCategory/${categoryData.id}`, formData);
      toast.success("Category updated successfully");
      handleCloseEdit();
      getData();
    } catch (err) {
      toast.error("Failed to update category");
      console.error("Error updating category:", err);
    }
  };

  const columns = [
    {
      field: "serialNo",
      flex: 1,
      headerName: "S.No",
      align: "center",
      headerAlign: "center",
    },
    {
      field: "image",
      headerName: "Image",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
          <img
            src={params.value}
            alt="Category"
            style={{
              width: 60,
              height: 60,
              objectFit: "cover",
              borderRadius: 8,
              border: "2px solid #e0e0e0",
            }}
          />
        </Box>
      ),
    },
    {
      field: "CategoryName",
      headerName: "Category Name",
      flex: 1,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Stack direction="row" spacing={1} justifyContent="center" mt={2}>
          <IconButton
            size="small"
            onClick={() => handleOpenEdit(params)}
            sx={{
              bgcolor: "#e3f2fd",
              "&:hover": { bgcolor: "#bbdefb" },
            }}
          >
            <EditIcon fontSize="small" sx={{ color: "#1976d2" }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row.id)}
            sx={{
              bgcolor: "#ffebee",
              "&:hover": { bgcolor: "#ffcdd2" },
            }}
          >
            <DeleteIcon fontSize="small" sx={{ color: "#d32f2f" }} />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const rows = data?.map((category, index) => ({
    id: category._id,
    image: category.image,
    CategoryName: category.CategoryName,
    serialNo: index + 1,
  }));

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f7fa" }}>
      <Header />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          ml: { md: "28px" },
          mt: "64px",
          p: 3,
        }}
      >
        <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
          {/* Header */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold" color="#1a237e">
                Category Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage product categories
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAdd}
              sx={{
                bgcolor: "#1976d2",
                "&:hover": { bgcolor: "#1565c0" },
                textTransform: "none",
                px: 3,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
              }}
            >
              Add Category
            </Button>
          </Box>

          {/* Stats Card */}
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Card sx={{ flex: 1, p: 2, bgcolor: "#e3f2fd", border: "1px solid #90caf9" }}>
              <Typography variant="body2" color="#1565c0">Total Categories</Typography>
              <Typography variant="h4" fontWeight="bold" color="#0d47a1">{data.length}</Typography>
            </Card>
          </Stack>

          {/* DataGrid */}
          <Box sx={{ height: 500, width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              rowHeight={80}
              sx={{
                border: "none",
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #f0f0f0",
                },
                "& .MuiDataGrid-columnHeaders": {
                  bgcolor: "#1565c0",
                  borderRadius: "8px 8px 0 0",
                  overflow: "hidden",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 600,
                  color: "#000",
                },
              }}
            />
          </Box>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ bgcolor: "#ffebee", color: "#c62828" }}>
            Confirm Delete
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Typography>Are you sure you want to delete this category? This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDeleteDialog(false)} sx={{ color: "#757575" }}>
              Cancel
            </Button>
            <Button
              onClick={() => confirmDelete(deleteItemId)}
              variant="contained"
              color="error"
              sx={{ textTransform: "none" }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Category Dialog */}
        <Dialog open={addDialog} onClose={handleCloseAdd} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight="bold">
              Add New Category
            </Typography>
            <IconButton onClick={handleCloseAdd} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Category Name"
                value={categoryData.name}
                onChange={(e) => setCategoryData({ ...categoryData, name: e.target.value })}
                placeholder="Enter category name"
              />
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Category Image
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{ py: 1.5, textTransform: "none" }}
                >
                  {categoryData.image ? categoryData.image.name : "Choose Image"}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleFileChange}
                  />
                </Button>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseAdd} sx={{ color: "#757575" }}>
              Cancel
            </Button>
            <Button
              onClick={handleAddCategory}
              variant="contained"
              sx={{ textTransform: "none" }}
            >
              Add Category
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Category Dialog */}
        <Dialog open={editDialog} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight="bold">
              Edit Category
            </Typography>
            <IconButton onClick={handleCloseEdit} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Category Name"
                value={categoryData.name}
                onChange={(e) => setCategoryData({ ...categoryData, name: e.target.value })}
                placeholder="Enter category name"
              />
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Category Image
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{ py: 1.5, textTransform: "none" }}
                >
                  {categoryData.image instanceof File ? categoryData.image.name : "Choose New Image"}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleFileChange}
                  />
                </Button>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseEdit} sx={{ color: "#757575" }}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateCategory}
              variant="contained"
              sx={{ textTransform: "none" }}
            >
              Update Category
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
